<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import EventsAPI from 'dashboard/api/planning/events';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EventSidebarSection from './EventSidebarSection.vue';

const props = defineProps({
  eventId: { type: String, required: true },
  shijiBookingLink: { type: Object, default: null },
  readOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['refreshed']);

const { t } = useI18n();
const refreshing = ref(false);

const isLinked = computed(() => !!props.shijiBookingLink?.booking_id);
const syncError = computed(() => props.shijiBookingLink?.sync_error || null);
const showCard = computed(() => isLinked.value || !!syncError.value);
const bookingId = computed(() => props.shijiBookingLink?.booking_id);
const bookingCode = computed(() => props.shijiBookingLink?.booking_code);
const bookingStatus = computed(() => props.shijiBookingLink?.booking_status);
const externalUrl = computed(() => props.shijiBookingLink?.url);

const copyValue = async value => {
  if (!value) return;

  try {
    await copyTextToClipboard(value);
    useAlert(t('PLANNING.SHOW.SHIJI_BOOKING_COPIED'));
  } catch {
    useAlert(t('PLANNING.SHOW.COPY_FAILED'));
  }
};

const refreshBooking = async () => {
  if (refreshing.value || !props.eventId) return;

  refreshing.value = true;
  try {
    const { data } = await EventsAPI.refreshShijiBooking(props.eventId);
    emit('refreshed', data);
    useAlert(t('PLANNING.SHOW.SHIJI_BOOKING_REFRESHED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('PLANNING.SHOW.SHIJI_BOOKING_REFRESH_FAILED')
    );
  } finally {
    refreshing.value = false;
  }
};
</script>

<template>
  <EventSidebarSection
    v-if="showCard"
    :title="t('PLANNING.SHOW.SHIJI_BOOKING_TITLE')"
    icon="i-lucide-plug"
    class="print:hidden"
  >
    <template v-if="isLinked && !readOnly" #actions>
      <button
        v-tooltip.top="t('PLANNING.SHOW.SHIJI_BOOKING_REFRESH')"
        type="button"
        class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-woot-600 dark:hover:text-white transition-colors disabled:opacity-50"
        :disabled="refreshing"
        @click="refreshBooking"
      >
        <Icon
          icon="i-lucide-refresh-cw"
          class="size-4"
          :class="{ 'animate-spin': refreshing }"
        />
      </button>
    </template>

    <div
      v-if="syncError"
      class="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 dark:border-red-800 dark:bg-red-900/20"
    >
      <p
        class="text-xs font-medium uppercase tracking-wide text-red-700 dark:text-red-300"
      >
        {{ t('PLANNING.SHOW.SHIJI_SYNC_ERROR') }}
      </p>
      <p class="text-sm text-red-800 dark:text-red-200 pt-1 break-words">
        {{ syncError }}
      </p>
    </div>

    <div v-if="bookingId" class="pt-3">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-500">
        {{ t('PLANNING.SHOW.SHIJI_BOOKING_ID') }}
      </p>
      <div class="flex items-start gap-1 pt-1">
        <p
          class="flex-1 text-sm font-mono text-slate-700 dark:text-slate-200 break-all"
        >
          {{ bookingId }}
        </p>
        <Button
          v-tooltip.top="t('PLANNING.SHOW.SHIJI_BOOKING_COPY')"
          ghost
          xs
          slate
          icon="i-lucide-copy"
          class="shrink-0"
          @click="copyValue(bookingId)"
        />
      </div>
    </div>

    <div v-if="bookingCode" class="pt-3">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-500">
        {{ t('PLANNING.SHOW.SHIJI_BOOKING_CODE') }}
      </p>
      <div class="flex items-center gap-1 pt-1">
        <p
          class="flex-1 text-base font-semibold text-slate-900 dark:text-white"
        >
          {{ bookingCode }}
        </p>
        <Button
          v-tooltip.top="t('PLANNING.SHOW.SHIJI_BOOKING_COPY')"
          ghost
          xs
          slate
          icon="i-lucide-copy"
          class="shrink-0"
          @click="copyValue(bookingCode)"
        />
      </div>
    </div>

    <div v-if="bookingStatus" class="pt-3">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-500">
        {{ t('PLANNING.SHOW.SHIJI_BOOKING_STATUS') }}
      </p>
      <p class="text-sm text-slate-700 dark:text-slate-200 pt-1">
        {{ bookingStatus }}
      </p>
    </div>

    <a
      v-if="externalUrl"
      :href="externalUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="inline-flex items-center gap-2 mt-4 px-3 py-2 text-sm font-medium rounded-lg text-woot-600 dark:text-woot-300 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 dark:hover:bg-woot-900/30 transition-colors"
    >
      <Icon icon="i-lucide-external-link" class="size-4" />
      {{ t('PLANNING.SHOW.SHIJI_BOOKING_OPEN') }}
    </a>
  </EventSidebarSection>
</template>
